import { useState, useEffect } from "react";

const SplitBackground = ({
  imageUrl,
  color = "#3b82f6",
  title,
  value,
  setCurrentSubSection,
  items,
}) => {
  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const hasCarousel = Array.isArray(items) && items.length > 0;

  useEffect(() => {
    if (!hasCarousel) return;
    const iv = setInterval(() => setIdx((p) => (p + 1) % items.length), 5000);
    return () => clearInterval(iv);
  }, [items, hasCarousel]);

  const current = hasCarousel ? items[idx] : { title, value };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setCurrentSubSection(title)}
      onKeyDown={(e) => e.key === "Enter" && setCurrentSubSection(title)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative overflow-hidden rounded-lg outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer"
      style={{
        display: "flex",
        height: 160,
        transition: "box-shadow 0.3s ease",
        // hovered → login-panel-glow equivalent; resting → login-panel
        boxShadow: hovered
          ? "0 24px 80px rgba(28,157,234,0.2)"
          : "0 12px 50px rgba(0,0,0,0.3)",
      }}
    >
      {/* Background photo */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transition: "transform 0.7s ease-out",
          transform: hovered ? "scale(1.06)" : "scale(1)",
        }}
      />

      {/* Color gradient overlay — photo legibility, comes from SECTION_CONFIG */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(to right, ${color} 0%, ${color}ee 20%, ${color}bb 45%, ${color}44 75%, transparent 100%)`,
          transition: "opacity 0.3s",
          opacity: hovered ? 0.95 : 0.88,
        }}
      />

      {/* Content */}
      <div
        className="font-poppins"
        style={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          display: "flex",
          alignItems: "center",
          padding: 24,
        }}
      >
        <div>
          {current.value !== undefined && (
            <p
              style={{
                fontSize: 36,
                fontWeight: 500, // font-medium — KPI number, display scale
                color: "rgba(255,255,255,0.95)",
                margin: "0 0 4px",
                transition: "transform 0.3s ease",
                transform: hovered ? "translateX(4px)" : "translateX(0)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {current.value}
            </p>
          )}
          {current.title && (
            <h2
              style={{
                fontSize: 16,
                fontWeight: 400, // font-normal
                color: "rgba(255,255,255,0.85)",
                margin: 0,
                transition: "transform 0.3s ease 30ms",
                transform: hovered ? "translateX(4px)" : "translateX(0)",
              }}
            >
              {current.title}
            </h2>
          )}

          {/* Carousel dots */}
          {hasCarousel && items.length > 1 && (
            <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
              {items.map((_, i) => (
                <div
                  key={i}
                  aria-hidden
                  style={{
                    height: 4,
                    borderRadius: 9999, // rounded-full for pill dots
                    transition: "all 0.3s",
                    width: i === idx ? 16 : 6,
                    background:
                      i === idx
                        ? "rgba(255,255,255,0.9)"
                        : "rgba(255,255,255,0.3)",
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hover chevron */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          right: 20,
          top: "50%",
          transition: "all 0.3s",
          opacity: hovered ? 1 : 0,
          transform: hovered
            ? "translateX(0) translateY(-50%)"
            : "translateX(-8px) translateY(-50%)",
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(255,255,255,0.7)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </div>
    </div>
  );
};

export default SplitBackground;
